import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { EXIF_FIELDS } from '../config/fieldDefinitions';
import {
  DEFAULT_CONFIG,
  validateConfig,
  mergeWithDefaults,
  createExportConfig,
  stripExportMetadata,
  migrateConfig
} from '../config/configSchema';

const STORAGE_KEY = 'exiftrator-field-config';

const FieldConfigContext = createContext();

export function FieldConfigProvider({ children }) {
  const [config, setConfig] = useState(DEFAULT_CONFIG);

  // Load configuration from localStorage on mount
  useEffect(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);

        // Migrate config if needed
        const migrated = migrateConfig(parsed);

        const { valid, errors } = validateConfig(migrated);

        if (valid) {
          // Merge with defaults to ensure new fields are present
          const merged = mergeWithDefaults(migrated);
          setConfig(merged);
        } else {
          console.error('Invalid stored configuration:', errors);
          // Fall back to defaults
          setConfig(DEFAULT_CONFIG);
        }
      }
    } catch (error) {
      console.error('Failed to load field configuration:', error);
      setConfig(DEFAULT_CONFIG);
    }
  }, []);

  // Save configuration to localStorage whenever it changes
  const saveToStorage = useCallback((newConfig) => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(newConfig));
    } catch (error) {
      console.error('Failed to save field configuration:', error);
    }
  }, []);

  // Update a single field's enabled state
  const updateFieldEnabled = useCallback((fieldId, enabled) => {
    setConfig(prev => {
      // If trying to disable, check if it's the last enabled field
      if (!enabled) {
        const enabledCount = Object.values(prev.enabledFields).filter(Boolean).length;
        if (enabledCount <= 1) {
          alert('At least one field must be enabled');
          return prev;
        }
      }

      const updated = {
        ...prev,
        enabledFields: {
          ...prev.enabledFields,
          [fieldId]: enabled
        }
      };
      saveToStorage(updated);
      return updated;
    });
  }, [saveToStorage]);

  // Update category order
  const updateCategoryOrder = useCallback((newOrder) => {
    setConfig(prev => {
      const updated = {
        ...prev,
        categoryOrder: newOrder
      };
      saveToStorage(updated);
      return updated;
    });
  }, [saveToStorage]);

  // Get fields by category (optionally filtered by enabled state)
  const getFieldsByCategory = useCallback((categoryId, onlyEnabled = false) => {
    return Object.values(EXIF_FIELDS).filter(field => {
      const matchesCategory = field.category === categoryId;
      if (!onlyEnabled) return matchesCategory;
      return matchesCategory && config.enabledFields[field.id];
    });
  }, [config.enabledFields]);

  // Get all enabled fields
  const getEnabledFields = useCallback(() => {
    return Object.values(EXIF_FIELDS).filter(field =>
      config.enabledFields[field.id]
    );
  }, [config.enabledFields]);

  // Get enabled fields in category order
  const getEnabledFieldsOrdered = useCallback(() => {
    const fields = [];
    config.categoryOrder.forEach(categoryId => {
      const categoryFields = getFieldsByCategory(categoryId, true);
      fields.push(...categoryFields);
    });
    return fields;
  }, [config.categoryOrder, getFieldsByCategory]);

  // Export configuration as JSON string
  const exportConfig = useCallback(() => {
    const exportData = createExportConfig(config);
    return JSON.stringify(exportData, null, 2);
  }, [config]);

  // Import configuration from JSON string
  const importConfig = useCallback((jsonString) => {
    try {
      const parsed = JSON.parse(jsonString);

      // Strip export metadata
      const cleanConfig = stripExportMetadata(parsed);

      // Migrate if needed
      const migrated = migrateConfig(cleanConfig);

      // Validate
      const { valid, errors } = validateConfig(migrated);

      if (!valid) {
        return { success: false, errors };
      }

      // Merge with defaults and save
      const merged = mergeWithDefaults(migrated);
      setConfig(merged);
      saveToStorage(merged);

      return { success: true, errors: [] };
    } catch (error) {
      return { success: false, errors: [error.message] };
    }
  }, [saveToStorage]);

  // Reset to default configuration
  const resetToDefaults = useCallback(() => {
    setConfig(DEFAULT_CONFIG);
    saveToStorage(DEFAULT_CONFIG);
  }, [saveToStorage]);

  // Check if a specific field is enabled
  const isFieldEnabled = useCallback((fieldId) => {
    return config.enabledFields[fieldId] === true;
  }, [config.enabledFields]);

  // Get count of enabled fields
  const enabledFieldCount = useMemo(() => {
    return Object.values(config.enabledFields).filter(Boolean).length;
  }, [config.enabledFields]);

  // Memoize context value to prevent unnecessary re-renders
  const value = useMemo(() => ({
    config,
    updateFieldEnabled,
    updateCategoryOrder,
    getFieldsByCategory,
    getEnabledFields,
    getEnabledFieldsOrdered,
    exportConfig,
    importConfig,
    resetToDefaults,
    isFieldEnabled,
    enabledFieldCount
  }), [
    config,
    updateFieldEnabled,
    updateCategoryOrder,
    getFieldsByCategory,
    getEnabledFields,
    getEnabledFieldsOrdered,
    exportConfig,
    importConfig,
    resetToDefaults,
    isFieldEnabled,
    enabledFieldCount
  ]);

  return (
    <FieldConfigContext.Provider value={value}>
      {children}
    </FieldConfigContext.Provider>
  );
}

export function useFieldConfig() {
  const context = useContext(FieldConfigContext);
  if (!context) {
    throw new Error('useFieldConfig must be used within FieldConfigProvider');
  }
  return context;
}
